import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { adminApi } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import type { User } from '@/types';
import { PlusIcon, TrashIcon, ChevronLeftIcon } from '@/components/Icons';
import LoadingSpinner from '@/components/LoadingSpinner';
import Modal from '@/components/Modal';

export default function AdminPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { showToast } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  // Form state
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'admin' | 'user'>('user');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    const res = await adminApi.listUsers();
    if (res.success && res.data) {
      setUsers(res.data);
    }
    setLoading(false);
  };

  const openAddModal = () => {
    setEditingUser(null);
    setLogin('');
    setPassword('');
    setRole('user');
    setError('');
    setShowModal(true);
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setLogin(user.login);
    setPassword('');
    setRole(user.role);
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!login.trim()) return;

    setSaving(true);
    setError('');

    if (editingUser) {
      const data: Record<string, string> = { role };
      if (login !== editingUser.login) data.login = login;
      if (password) data.password = password;

      const res = await adminApi.updateUser(editingUser.id, data);
      if (res.success) {
        await fetchUsers();
        setShowModal(false);
        showToast('Пользователь обновлён', 'success');
      } else {
        setError(res.error || 'Не удалось обновить пользователя');
      }
    } else {
      if (!password) {
        setError('Пароль обязателен для новых пользователей');
        setSaving(false);
        return;
      }

      const res = await adminApi.createUser({ login, password, role });
      if (res.success) {
        await fetchUsers();
        setShowModal(false);
        showToast('Пользователь создан', 'success');
      } else {
        setError(res.error || 'Не удалось создать пользователя');
      }
    }

    setSaving(false);
  };

  const handleDelete = async (user: User) => {
    if (user.id === currentUser?.id) {
      showToast('Нельзя удалить свой аккаунт', 'error');
      return;
    }

    if (!confirm(`Удалить пользователя "${user.login}"? Все данные будут удалены.`)) {
      return;
    }

    const res = await adminApi.deleteUser(user.id);
    if (res.success) {
      await fetchUsers();
      setShowModal(false);
      showToast('Пользователь удалён', 'success');
    } else {
      showToast(res.error || 'Не удалось удалить пользователя', 'error');
    }
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="page-container">
      <header className="page-header flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <h1 className="page-title ml-2">Администрирование</h1>
        </div>
        <button 
          onClick={openAddModal} 
          className="w-9 h-9 rounded-full bg-primary-600 text-white flex items-center justify-center hover:bg-primary-700 transition-colors shadow-sm"
        >
          <PlusIcon className="w-5 h-5" />
        </button>
      </header>

      <div className="p-4 space-y-6">
        {/* Users */}
        <section>
          <h2 className="section-title">Пользователи</h2>
          <div className="card divide-y divide-gray-100">
            {users.map((user) => (
              <div
                key={user.id}
                className="flex items-center justify-between p-4"
              >
                <div>
                  <p className="font-medium text-gray-900">
                    {user.login}
                    {user.id === currentUser?.id && (
                      <span className="ml-2 text-xs text-primary-600">(вы)</span>
                    )}
                  </p>
                  <p className="text-sm text-gray-500">
                    <span className="capitalize">{user.role}</span> - Создан{' '}
                    {formatDate(user.created_at)}
                  </p>
                </div>
                {user.id !== currentUser?.id && (
                  <button
                    onClick={() => openEditModal(user)}
                    className="btn-ghost btn-sm"
                  >
                    Изменить
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>

      </div>

      {/* Add/Edit User Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingUser ? 'Редактировать' : 'Новый пользователь'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 text-sm text-red-700 bg-red-50 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="login" className="label">
              Логин
            </label>
            <input
              id="login"
              type="text"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              className="input"
              placeholder="Введите логин"
              autoCapitalize="none"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="label">
              Пароль {editingUser && '(оставьте пустым чтобы не менять)'}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              placeholder={editingUser ? 'Новый пароль' : 'Введите пароль'}
              minLength={8}
              required={!editingUser}
            />
          </div>

          <div>
            <label htmlFor="role" className="label">
              Роль
            </label>
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value as 'admin' | 'user')}
              className="input"
            >
              <option value="user">Пользователь</option>
              <option value="admin">Администратор</option>
            </select>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="btn-secondary flex-1"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={saving || !login.trim()}
              className="btn-primary flex-1"
            >
              {saving ? <LoadingSpinner size="sm" className="text-white" /> : 'Сохранить'}
            </button>
          </div>

          {editingUser && editingUser.id !== currentUser?.id && (
            <button
              type="button"
              onClick={() => handleDelete(editingUser)}
              className="btn-danger w-full"
            >
              <TrashIcon className="w-4 h-4 mr-2" />
              Удалить пользователя
            </button>
          )}
        </form>
      </Modal>
    </div>
  );
}
